import { CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState } from "react";

export interface TestResult {
  testId: string;
  description: string;
  passed: boolean;
  actualOutput?: any;
  expectedOutput?: any;
  errorMessage?: string;
  executionTime?: number;
  isHidden?: boolean;
}

interface TestResultsPanelProps {
  results: TestResult[];
  totalTests: number;
  passedTests: number;
}

export default function TestResultsPanel({
  results,
  totalTests,
  passedTests,
}: TestResultsPanelProps) {
  const [showHidden, setShowHidden] = useState(false);
  const visibleResults = results.filter((r) => !r.isHidden);
  const hiddenResults = results.filter((r) => r.isHidden);
  const allPassed = passedTests === totalTests;

  return (
    <Card className="bg-card/50 backdrop-blur border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            Test Results
            <Badge variant={allPassed ? "default" : "secondary"}>
              {passedTests}/{totalTests} Passed
            </Badge>
          </CardTitle>
          {hiddenResults.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHidden(!showHidden)}
            >
              {showHidden ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Hide Hidden Tests
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Show Hidden Tests
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {visibleResults.map((result) => (
          <TestResultItem key={result.testId} result={result} />
        ))}

        {showHidden && hiddenResults.length > 0 && (
          <>
            <div className="border-t border-border pt-3 mt-3">
              <p className="text-sm text-muted-foreground mb-2">
                Hidden Test Cases
              </p>
            </div>
            {hiddenResults.map((result) => (
              <TestResultItem key={result.testId} result={result} />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TestResultItem({ result }: { result: TestResult }) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="p-3 rounded-lg border border-border bg-background/50">
      <div className="flex items-start gap-3">
        {result.passed ? (
          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
        ) : (
          <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-sm font-medium">{result.description}</p>
            {result.executionTime && (
              <Badge variant="outline" className="text-xs">
                {result.executionTime}ms
              </Badge>
            )}
          </div>

          {!result.passed && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? "Hide" : "Show"} Details
            </Button>
          )}

          {showDetails && !result.passed && (
            <div className="mt-2 space-y-2 text-xs">
              {result.errorMessage && (
                <div className="p-2 rounded bg-destructive/10 text-destructive">
                  <p className="font-medium mb-1">Error:</p>
                  <pre className="whitespace-pre-wrap break-all">
                    {result.errorMessage}
                  </pre>
                </div>
              )}
              {result.expectedOutput !== undefined && (
                <div className="p-2 rounded bg-muted">
                  <p className="font-medium mb-1">Expected:</p>
                  <pre className="whitespace-pre-wrap break-all">
                    {JSON.stringify(result.expectedOutput, null, 2)}
                  </pre>
                </div>
              )}
              {result.actualOutput !== undefined && (
                <div className="p-2 rounded bg-muted">
                  <p className="font-medium mb-1">Actual:</p>
                  <pre className="whitespace-pre-wrap break-all">
                    {JSON.stringify(result.actualOutput, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
